import { showToast } from '@design-system';
import { GetUsersResponse } from '@services';
import { useEffect, useRef, useState } from 'react';
import { TaskData } from './PointageTask';

type UsePointageTaskProps = {
    initialTask: TaskData;
    operators: GetUsersResponse['operators'];
    onTaskChange: (taskId: string | number, taskData: TaskData) => void;
    taskId: string | number;
    summedHours?: number;
};

export const usePointageTask = ({ initialTask, operators, onTaskChange, taskId, summedHours }: UsePointageTaskProps) => {
    const hoursOptions = [
        { id: '0', label: '0 heures' },
        { id: '0.5', label: '30 minutes' },
        { id: '1', label: '1 heure' },
        { id: '1.5', label: '1 heure 30' },
        { id: '2', label: '2 heures' },
        { id: '2.5', label: '2 heures 30' },
        { id: '3', label: '3 heures' },
        { id: '3.5', label: '3 heures 30' },
        { id: '4', label: '4 heures' },
        { id: '4.5', label: '4 heures 30' },
        { id: '5', label: '5 heures' },
        { id: '5.5', label: '5 heures 30' },
        { id: '6', label: '6 heures' },
        { id: '6.5', label: '6 heures 30' },
        { id: '7', label: '7 heures' },
        { id: '7.5', label: '7 heures 30' },
        { id: '8', label: '8 heures' },
        { id: '8.5', label: '8 heures 30' },
        { id: '9', label: '9 heures' },
        { id: '9.5', label: '9 heures 30' },
        { id: '10', label: '10 heures' },
        { id: '10.5', label: '10 heures 30' },
        { id: '11', label: '11 heures' },
        { id: '11.5', label: '11 heures 30' },
        { id: '12', label: '12 heures' }
    ];

    const [taskData, setTaskData] = useState<TaskData>({
        operator_id: initialTask.operator_id || '',
        worksite_id: initialTask.worksite_id || '',
        hours: initialTask.hours || '',
        type_id: initialTask.type_id || '',
        reason: initialTask.reason,
        state_id: initialTask.state_id
    });
    const [showModal, setShowModal] = useState<boolean>(false);

    const prevTaskDataRef = useRef<TaskData>(taskData);

    const [operatorsOptions, setOperatorsOptions] = useState<
        (GetUsersResponse['operators'][number] & {
            id: string;
            label: string;
        })[]
    >([]);

    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);

    const handleFieldChange = (field: keyof TaskData, value: string) => {
        if (field === 'operator_id') {
            setWorksitesOptions([]);
            setTaskData((prev) => ({
                ...prev,
                [field]: value,
                worksite_id: ''
            }));

            const selectedOperator = operators.find((op) => op.id_operator === value);
            if (selectedOperator) {
                const worksites = selectedOperator.worksites.map((worksite) => ({
                    id: worksite.id,
                    label: worksite.name
                }));
                setWorksitesOptions(worksites);
            }
        } else if (field === 'hours') {
            if (summedHours && value !== '') {
                const totalHours = Number(value) + summedHours;
                if (totalHours > 12) {
                    showToast({
                        message: 'Vous ne pouvez pas pointer plus de 12 heures par jour',
                        type: 'error'
                    });
                    setTaskData((prev) => ({
                        ...prev,
                        hours: '0'
                    }));
                    return;
                }
            }
            setTaskData((prev) => ({
                ...prev,
                hours: value
            }));
        } else {
            setTaskData((prev) => ({
                ...prev,
                [field]: value
            }));
        }
    };

    useEffect(() => {
        const hasChanged =
            prevTaskDataRef.current.operator_id !== taskData.operator_id ||
            prevTaskDataRef.current.worksite_id !== taskData.worksite_id ||
            prevTaskDataRef.current.hours !== taskData.hours ||
            prevTaskDataRef.current.type_id !== taskData.type_id;

        if (hasChanged) {
            prevTaskDataRef.current = taskData;
            onTaskChange(taskId, taskData);
        }
    }, [taskData, onTaskChange, taskId]);

    useEffect(() => {
        if (operators) {
            setOperatorsOptions(
                operators.map((operator) => ({
                    ...operator,
                    id: operator.id_operator,
                    label: operator.name
                }))
            );
        }
    }, [operators]);

    useEffect(() => {
        if (operators && initialTask.operator_id) {
            const selectedOperator = operators.find((op) => op.id_operator === initialTask.operator_id);

            if (selectedOperator) {
                const worksites = selectedOperator.worksites.map((worksite) => ({
                    id: worksite.id,
                    label: worksite.name
                }));
                setWorksitesOptions(worksites);
            }
        }
    }, [operators, initialTask]);

    return {
        hoursOptions,
        taskData,
        operatorsOptions,
        worksitesOptions,
        handleFieldChange,
        showModal,
        setShowModal
    };
};
